import download from "downloadjs";
import {
    ExportTableNames,
    ExportTypes,
    OrganizationDownloadUsage,
} from "~/modules/downloads/types";
import { useToaster } from "~/modules/toaster/hooks/useToaster";
import { api } from "~/utils/api";
import { useUser } from "~/modules/user/hooks/useUser";
import { useState } from "react";
import * as Analytics from "../../analytics";
import { ApiQuery } from "~/modules/filters/types";
import { DataExportModal } from "../DataExportModal";
import { FeatureNames } from "~/modules/feature_flag/flags";
import { useFeatureFlag } from "~/modules/feature_flag/hooks";
import { LoginRequiredModal } from "../LoginRequiredModal";
import { UpgradeToProModal } from "~/modules/billing/components/UpgradeToProModal";
import { PlanTypes } from "~/modules/user/store/types";

// // // //

interface ExportProps {
    query: ApiQuery;
    exportTableName: ExportTableNames;
    exportFilename?: string;
}

interface ChildProps {
    isLoggedIn: boolean;
    isLoading: boolean;
    exportCsv: (exportProps: ExportProps) => void;
    exportExcel: (exportProps: ExportProps) => void;
}

interface GetDownloadUrlResponse {
    download_url: string | null;
    usage: OrganizationDownloadUsage | null;
    exportProps: ExportProps & { exportType: ExportTypes };
}

/**
 * Encapsulates shared logic for integrating custom CSV/Excel downloads (i.e. not statically generated nightly exports)
 * Includes authentication check + data export fetching + user feedback toasters
 */
export function DataExportProvider(props: {
    ignoreFeatureFlag?: boolean;
    children: (childProps: ChildProps) => React.ReactNode;
}) {
    const { children, ignoreFeatureFlag = false } = props;
    const { isLoggedIn, user } = useUser();
    const { addToaster } = useToaster();

    const [showingModal, setShowingModal] = useState(false);
    const [showingInfoModal, setShowingInfoModal] = useState(false);
    const [apiResponse, setApiResponse] =
        useState<GetDownloadUrlResponse | null>(null);

    const enabled = useFeatureFlag(FeatureNames.enableDataDownloads);

    // Disable feature if user isn't logged in
    let disableFeature = !isLoggedIn;

    // Add feature flag check only if we're not ignoring it
    if (!ignoreFeatureFlag) {
        disableFeature = disableFeature || !enabled;
    }

    // Define loading state
    const exportReq = api.downloads.getDownloadUrl.useMutation();
    const { isPending: isLoading } = exportReq;

    // Flag to "pre-confirm" the download when the user's plan supports unlimited downloads
    const autoConfirmDownload = [
        PlanTypes.PREMIUM_ALL_ACCESS,
        PlanTypes.TRIAL_ALL_ACCESS,
        PlanTypes.PREMIUM_ACADEMIC,
    ].includes(user.organization.plan.type);

    const exportData = (
        exportProps: ExportProps & {
            exportType: ExportTypes;
            confirmDownload: boolean;
        },
    ) => {
        const {
            query,
            exportTableName,
            exportFilename,
            exportType,
            confirmDownload,
        } = exportProps;

        // Skip export and show modal if feature disabled
        if (disableFeature) {
            Analytics.trackExportAttempted({
                type: exportType,
                source: exportTableName,
            });
            setShowingModal(true);
            return;
        }

        // Show info modal
        if (!showingInfoModal) {
            // NOTE - we do setApiResponse(null) here because a non-null value shows a different view in the DownloadInfoModal component
            setApiResponse(null);
            setShowingInfoModal(true);
        }

        // Add initial Toaster so user gets some immediate feedback that their download is starting
        if (confirmDownload) {
            addToaster({
                variant: "primary",
                message: "Data Download is starting",
            });
        }

        // Fetch CSV/Excel export
        exportReq
            .mutateAsync({
                query,
                exportType,
                exportTableName,
                exportFilename,
                organizationID: user.organization.id,
                confirmDownload,
            })
            .then((resp: GetDownloadUrlResponse) => {
                const { download_url } = resp;

                // Store download URL and usage in state
                setApiResponse(resp);

                // If download_url is present -> the export was successful
                if (download_url) {
                    // Track the success
                    Analytics.trackExportSuccess({
                        type: exportType,
                        source: exportTableName,
                    });

                    // Show success toaster
                    addToaster({
                        message:
                            exportType === ExportTypes.csv
                                ? "Successfully generated CSV Export"
                                : "Successfully generated Excel Export",
                    });

                    // Auto-download the file
                    download(download_url);
                }
            })
            .catch(() => {
                // Track the error
                Analytics.trackExportError({
                    type: exportType,
                    source: exportTableName,
                });

                // Show error toaster
                addToaster({
                    variant: "danger",
                    message:
                        exportType === ExportTypes.csv
                            ? "Error fetching CSV Export"
                            : "Error fetching Excel Export",
                });
            });
    };

    return (
        <>
            {children({
                isLoggedIn,
                isLoading,
                exportCsv: (exportProps) => {
                    exportData({
                        ...exportProps,
                        exportType: ExportTypes.csv,
                        confirmDownload: autoConfirmDownload,
                    });
                },
                exportExcel: (exportProps) => {
                    exportData({
                        ...exportProps,
                        exportType: ExportTypes.excel,
                        confirmDownload: autoConfirmDownload,
                    });
                },
            })}

            {disableFeature && ignoreFeatureFlag === false && (
                <UpgradeToProModal
                    show={showingModal}
                    onHide={() => {
                        setShowingModal(false);
                    }}
                />
            )}

            {disableFeature && ignoreFeatureFlag === true && (
                <LoginRequiredModal
                    allowScroll
                    show={showingModal}
                    content={{
                        title: "Log in to Download",
                        hideFeaturesSection: true,
                    }}
                    onHide={() => {
                        setShowingModal(false);
                    }}
                />
            )}

            {!disableFeature && (
                <DataExportModal
                    show={showingInfoModal}
                    usage={apiResponse?.usage ?? null}
                    isLoadingDownloadUrl={isLoading}
                    downloadUrl={apiResponse?.download_url ?? null}
                    planType={user.organization.plan.type}
                    onHide={() => {
                        setShowingInfoModal(false);
                    }}
                    onConfirmDownload={() => {
                        // Skip export if no prior apiResponse is present
                        if (!apiResponse) return;

                        // Track the confirmed download event
                        Analytics.trackExportConfirmed({
                            type: apiResponse.exportProps.exportType,
                            source: apiResponse.exportProps.exportTableName,
                        });

                        // Fire off data export with confirmDownload set to true
                        exportData({
                            ...apiResponse.exportProps,
                            confirmDownload: true,
                        });
                    }}
                />
            )}
        </>
    );
}
